import { parseLegacyHtmlFragmentNodes } from '@/msgdeskRuntime/legacyBridge/LegacyHtmlAst';
import type { LegacyParsedElementNode, LegacyParsedNode } from '@/msgdeskRuntime/legacyBridge/LegacyHtmlAstTypes';
import {
  resolveLegacyUiCommand,
  type HumanLegacyCommand,
  type LegacyUiNormalizedOp,
} from '@/msgdeskRuntime/legacyBridge/CommandRouter';
import {
  normalizeLegacyRjqOp,
  type LegacyRjqNormalizedOp,
} from '@/msgdeskRuntime/legacyBridge/LegacyRjqFacade';
import { getMsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime';
import type { LegacyMoveDestination } from '@/msgdeskRuntime/contracts/itemMovements';
import {
  EDITOR_SETTING_NAMES,
  type EditorFormModel,
  type EditorSettingValue,
} from '@/msgdeskRuntime/contracts/editorModel';
import {
  defaultSubUiState,
  extractElIdFromHtmlId,
} from './types'
import { legacyOperationGate } from './legacyOperationGate'
import { extractPayloadSpanJson } from './payloadJson'
import {
  mergeLegacyChildrenMaps,
  projectLegacySubsTree,
} from './legacyTree'
import {
  configureProjectionSync,
  currentLegacyPagingMode,
  projectionModeFromMenuSwitcher,
  syncLegacyModeProjection,
  syncProjectionAfterLegacyEvent,
} from './projectionSync'
import type {
  ChromeElState,
  EditorAttachmentState,
  EditorFormInputState,
  LegacyDataContainerKind,
  LegacyDataContainerState,
  LegacySectionInput,
  MoveCheckedOptions,
  MsgObjCommandTarget,
  MsgObjSemanticCommand,
  SectionPagingSummary,
  SubsElementNodeData,
  SubsLoadingState,
  SubsNodeData,
  SubsSection,
  SubsStoreState,
  SubUiState,
} from './types'
import type { LegacyHandlerName } from '../contracts/legacyEvents'
import type { PooClientProjection } from '../../contracts/poo'

function emptyState(): SubsStoreState {
  return {
    nodesById: {},
    nodesByElId: {},
    htmlIdToNodeId: {},
    childrenMap: {},
    activeIds: [],
    syncVersion: 0,
    visibilityMap: {},
    subStates: {},
    nextNodeSeq: 1,
    templateData: { text: {}, attrs: {}, elementToText: {} },
    legacyData: {},
    renderProjection: {
      rootIds: { answers: [], submsges: [] },
      currentPage: {},
      mode: 'msg',
      activeSection: 'answers',
    },
    chromeByContract: {},
    subsLoading: null,
    subChildrenHidden: {},
  };
}

function parseJsonPayload(value: string | null | undefined): any {
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function cloneStoreData<T>(value: T, depth = 0): T {
  if (value == null || typeof value !== 'object') return value;
  if (depth > 8) return undefined as T;
  if (Array.isArray(value)) return value.map(item => cloneStoreData(item, depth + 1)) as T;

  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'function') continue;
    out[key] = cloneStoreData(item, depth + 1);
  }
  return out as T;
}

function phpModelItemElId(item: any): number {
  const candidates = [item?.ttujtu, item?.last_elid, item?.raw_id, item?.id];
  for (const candidate of candidates) {
    const id = Number(candidate);
    if (Number.isInteger(id) && id > 0) return id;
  }
  return 0;
}

function phpModelSectionItems(section: any): any[] {
  return Array.isArray(section?.items) ? section.items : [];
}

function phpModelSectionIds(section: any): number[] {
  return phpModelSectionItems(section)
    .map(phpModelItemElId)
    .filter((id, index, ids): id is number => id > 0 && ids.indexOf(id) === index);
}

function idsFromLegacySubsObject(subs: any, isSubmsges: boolean): number[] {
  if (!subs || typeof subs !== 'object') return [];
  return Object.entries(subs)
    .map(([key, value]) => {
      const id = Number(key);
      if (!Number.isInteger(id) || id <= 0) return 0;
      const section = boolFromLegacy((value as any)?.is_submsges_section);
      return section === isSubmsges ? id : 0;
    })
    .filter((id, index, ids): id is number => id > 0 && ids.indexOf(id) === index);
}

function phpModelSubsById(subsModel: any): Record<number, any> {
  const byId: Record<number, any> = {};
  for (const section of [subsModel?.answers, subsModel?.submsges]) {
    for (const item of phpModelSectionItems(section)) {
      const elId = phpModelItemElId(item);
      if (elId > 0) byId[elId] = item;
    }
  }
  return byId;
}

function firstDefined<T>(...values: T[]): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  }
  return undefined;
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const text = firstString(value);
    if (text && text.trim() !== '') return text;
  }
  return undefined;
}

function firstNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return undefined;
}

function boolFromLegacy(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off', ''].includes(normalized)) return false;
  }
  return undefined;
}

function readRuntimeMainMsgData(): any | undefined {
  try {
    return getMsgDeskRuntime().facade.msgData(0);
  } catch {
    return undefined;
  }
}

function readRuntimeMsgInputs(elId = 0): Record<string, unknown> | undefined {
  try {
    return getMsgDeskRuntime().facade.msgInputs(elId);
  } catch {
    return undefined;
  }
}

function cleanPreviewText(value: string | null | undefined): string | undefined {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text || undefined;
}

function firstElementText(root: Element, selectors: string[]): string | undefined {
  for (const selector of selectors) {
    const el = root.querySelector(selector);
    const text = cleanPreviewText(el?.textContent);
    if (text) return text;
  }
  return undefined;
}

function firstElementHtml(root: Element, selectors: string[]): string | undefined {
  for (const selector of selectors) {
    const el = root.querySelector(selector);
    const html = sanitizeLegacySubSnippetHtml(String(el?.innerHTML ?? '').trim());
    if (html) return html;
  }
  return undefined;
}

const LEGACY_SUB_SNIPPET_CLASS_MAP: Record<string, string[]> = {
  'mcss-list_compact_head_txt-cssm': ['md-sub-compact-head-text'],
  'mcss-youneedapin-cssm': ['md-sub-pin-required'],
  'mcss-ic_clprt_lists-cssm': ['md-sub-inline-icon'],
  'mcss-ic_msg_time-cssm': ['md-sub-time-icon'],
  'mcss-ic_str-cssm': ['md-sub-status-text'],
};

function normalizedSubSnippetClasses(classValue: string): string[] {
  const next: string[] = [];
  for (const token of classValue.split(/\s+/).filter(Boolean)) {
    const mapped = LEGACY_SUB_SNIPPET_CLASS_MAP[token];
    if (mapped) {
      next.push(...mapped);
      continue;
    }
    if (token.startsWith('mcss-')) continue;
    next.push(token);
  }
  return Array.from(new Set(next));
}

function sanitizeLegacySubSnippetHtml(html: string | undefined): string | undefined {
  const raw = String(html ?? '').trim();
  if (!raw) return undefined;

  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return raw;
    root.querySelectorAll<HTMLElement>('[class]').forEach(el => {
      const classes = normalizedSubSnippetClasses(el.getAttribute('class') || '');
      if (classes.length) el.setAttribute('class', classes.join(' '));
      else el.removeAttribute('class');
    });
    return root.innerHTML.trim() || undefined;
  }

  return raw.replace(/\sclass=(["'])(.*?)\1/g, (_match, quote: string, classValue: string) => {
    const classes = normalizedSubSnippetClasses(classValue);
    return classes.length ? ` class=${quote}${classes.join(' ')}${quote}` : '';
  }).trim() || undefined;
}

function isElementNode(node: LegacyParsedNode): node is LegacyParsedElementNode {
  return node.kind === 'element';
}

function editorTextField(name: string, value: unknown): EditorFormInputState | null {
  if (value === undefined || value === null) return null;
  return { name, tag: 'input', type: 'text', value: String(value) };
}

function editorTextareaField(name: string, value: unknown): EditorFormInputState | null {
  if (value === undefined || value === null) return null;
  return { name, tag: 'textarea', value: typeof value === 'object' && value && 'msg_text' in value ? String((value as any).msg_text ?? '') : String(value) };
}

function editorCheckboxField(name: string, checked: unknown): EditorFormInputState {
  return { name, tag: 'input', type: 'checkbox', value: '1', checked: !!checked };
}

function editorSelectField(name: string, value: unknown): EditorFormInputState | null {
  if (value === undefined || value === null) return null;
  // m31 display rule: a stored 0 (pair checkbox unchecked) still DISPLAYS
  // option 1 (`inputs[i].val(is_disabled ? 1 : values[i])`). Without the clamp
  // the select has no selected option and reads back '' -> NaN into the rough.
  const numeric = Number(value);
  const display = Number.isFinite(numeric) && numeric < 1 ? '1' : String(value);
  return { name, tag: 'select', value: display, selectedValues: [display] };
}

function putEditorField(inputs: Record<string, EditorFormInputState>, field: EditorFormInputState | null): void {
  if (!field) return;
  inputs[field.name] = field;
}

function currentLegacyEditorMode(): string {
  return String(getMsgDeskRuntime().facade.deskMode() || '');
}

function collectEditorFormInputsFromMsgInputs(msgInputs: any, msgData?: any): Record<string, EditorFormInputState> {
  const inputs: Record<string, EditorFormInputState> = {};
  if (!msgInputs || typeof msgInputs !== 'object') return inputs;

  const editorMode = currentLegacyEditorMode();
  const isNewEditorMode = editorMode === 'new_answer' || editorMode === 'new_submsg' || editorMode === 'new_msg';
  const body = Array.isArray(msgInputs.body) ? msgInputs.body : [];
  const bodyTextarea = Array.isArray(msgInputs.body_textarea) ? msgInputs.body_textarea : [];
  const toAddress = Array.isArray(msgInputs.to_address) ? msgInputs.to_address : [];
  const more = Array.isArray(msgInputs.more_settings) ? msgInputs.more_settings : [];
  const protect = Array.isArray(msgInputs.protect) ? msgInputs.protect : [];
  const ruling = Array.isArray(msgInputs.ruling) ? msgInputs.ruling : [];

  const nameKey = isNewEditorMode ? '' : firstString(bodyTextarea[0], bodyTextarea[3]);
  putEditorField(inputs, editorTextField('name_key_smart', nameKey));
  putEditorField(inputs, editorTextareaField('name_key', nameKey));
  putEditorField(inputs, editorTextField('code_key', isNewEditorMode ? '' : body[0]));
  putEditorField(inputs, editorTextField('subject', isNewEditorMode ? '' : body[1]));
  putEditorField(inputs, editorTextField('to_address', isNewEditorMode ? '' : toAddress[0]));
  putEditorField(inputs, editorTextareaField('msg_text', isNewEditorMode
    ? ''
    : firstNonEmptyString(
      typeof bodyTextarea[1] === 'object' && bodyTextarea[1] ? (bodyTextarea[1] as any).msg_text : bodyTextarea[1],
      bodyTextarea[2],
      msgData?.html,
      msgData?.body_html,
      msgData?.txt_html,
      msgData?.body,
      msgData?.text,
    )));

  const optionPairs: Array<[number, string, string]> = [
    [0, 'is_view', 'is_view_combo'],
    [2, 'is_view_msges', 'is_view_msges_combo'],
    [4, 'is_view_answers', 'is_view_answers_combo'],
    [6, 'is_forbid', 'is_forbid_combo'],
    [8, 'is_fold', 'is_fold_combo'],
    [10, 'is_freeze', 'is_freeze_combo'],
    [12, 'is_hide', 'is_hide_combo'],
  ];
  optionPairs.forEach(([index, checkName, comboName]) => {
    if (index in more) inputs[checkName] = editorCheckboxField(checkName, more[index]);
    putEditorField(inputs, editorSelectField(comboName, more[index + 1]));
  });

  [
    [14, 'is_hide_parents'],
    [15, 'is_one_parent'],
    [16, 'is_avatar'],
    [17, 'is_full_tree'],
    [18, 'is_force_email'],
    [19, 'is_quote'],
    [20, 'is_force_pins'],
    [21, 'is_to_remove'],
    [22, 'is_left_menu'],
    [23, 'is_super'],
    [24, 'is_hidden'],
    [25, 'is_view_count'],
  ].forEach(([index, name]) => {
    if ((index as number) in more) inputs[name as string] = editorCheckboxField(name as string, more[index as number]);
  });

  for (let index = 0; index <= 5; index += 1) {
    putEditorField(inputs, editorSelectField(`is_view_count_${index}`, more[26 + index]));
  }

  putEditorField(inputs, editorTextField('is_page_options_color', more[32]));
  putEditorField(inputs, editorTextField('is_page_options_margin', more[33]));
  putEditorField(inputs, editorTextField('is_page_options_padding', more[34]));

  const showPin = Array.isArray(protect[0]) ? protect[0] : [];
  const editPin = Array.isArray(protect[1]) ? protect[1] : [];
  inputs.is_show_pin = editorCheckboxField('is_show_pin', showPin[0]);
  putEditorField(inputs, editorTextField('show_pin', showPin[1]));
  inputs.is_edit_pin = editorCheckboxField('is_edit_pin', editPin[0]);
  putEditorField(inputs, editorTextField('edit_pin', editPin[1]));

  putEditorField(inputs, { name: 'time_start', tag: 'input', type: 'hidden', value: String(firstDefined(msgInputs.time_start, ruling[0], 0)) });
  putEditorField(inputs, { name: 'time_stop', tag: 'input', type: 'hidden', value: String(firstDefined(msgInputs.time_stop, ruling[1], 0)) });
  // Slot 2 is the frozen legacy placeholder; the three named flags start at 3.
  inputs.is_rule_start = editorCheckboxField('is_rule_start', ruling[3]);
  inputs.is_rule_stop = editorCheckboxField('is_rule_stop', ruling[4]);
  inputs.is_hide_time_locked = editorCheckboxField('is_hide_time_locked', ruling[5]);

  return inputs;
}

function mergeEditorFormInputs(
  fallbackInputs: Record<string, EditorFormInputState>,
  primaryInputs: Record<string, EditorFormInputState>,
): Record<string, EditorFormInputState> {
  const merged = { ...fallbackInputs };
  for (const [name, field] of Object.entries(primaryInputs)) {
    const current = merged[name];
    const valueIsUseful = field.value !== undefined;
    const stateIsUseful = field.checked !== undefined || field.selectedValues !== undefined || field.type === 'hidden';
    if (valueIsUseful || stateIsUseful || !current) merged[name] = field;
  }
  return merged;
}

function isVisualOnlyEditorFragmentSelector(selector: string): boolean {
  return selector.includes('mcss-new_to_msg_list-cssm')
    || selector.includes('mcss-avatar_div-cssm');
}

function isAttachmentGalleryFragmentSelector(selector: string): boolean {
  return selector.includes('mcss-att_glr-cssm')
    || selector.includes('mcss-attaches_gallery-cssm');
}

function parseEditorAttachmentsFromHtml(html: string): EditorAttachmentState[] {
  const root = getMsgDeskRuntime().createElement('div');
  if (!html || !root) return [];
  root.innerHTML = html;
  const labels = Array.from(root.querySelectorAll<HTMLElement>('.mcss-file_label-cssm'));

  return labels.map((label, index) => {
    const img = label.querySelector<HTMLImageElement>('img');
    const filename = label.querySelector<HTMLElement>('.mcss-file_name-cssm')?.textContent?.trim() || '';
    const progress = label.querySelector<HTMLElement>('.mcss-rm_file_2-cssm')?.textContent?.trim() || '';
    const remove = label.querySelector<HTMLElement>('.mcss-rm_file-cssm');
    const overlay = label.querySelector<HTMLElement>('.mcss-img_holder_2-cssm');
    const id = label.getAttribute('att_id') || undefined;

    return {
      id,
      src: img?.getAttribute('src') || 'css/cssimgs/attach.png',
      filename,
      progress,
      removable: !!id || !!remove?.getAttribute('onclick'),
      toggleable: !!id || !!overlay?.getAttribute('onclick'),
      isUploadPlaceholder: !id && index === 0,
      isAvatar: html.includes('mcss-avatar_glr-cssm'),
    };
  });
}

function hasRealEditorAttachments(attachments?: EditorAttachmentState[]): boolean {
  return !!attachments?.some(attachment => !attachment.isUploadPlaceholder && !!attachment.id);
}

function normalizeEditorAttachmentsUpdate(
  current: EditorAttachmentState[] | undefined,
  next: EditorAttachmentState[],
): EditorAttachmentState[] {
  if (hasRealEditorAttachments(current) && !hasRealEditorAttachments(next)) return current || [];
  return next;
}

function collectEditorAttachmentsFromRuntime(): EditorAttachmentState[] {
  const rough = getMsgDeskRuntime().facade.editorRough();
  const msgData = readRuntimeMainMsgData();
  const attsArr = rough?.atts_arr || msgData?.atts_arr;
  if (!Array.isArray(attsArr)) return [];

  const attachments: EditorAttachmentState[] = [{
    src: 'css/cssimgs/attach.png',
    filename: '',
    progress: '0%',
    removable: false,
    toggleable: false,
    isUploadPlaceholder: true,
  }];

  for (let index = 2; index < attsArr.length; index += 1) {
    const entry = attsArr[index];
    if (!Array.isArray(entry)) continue;
    const v2Entry = entry as typeof entry & { __v2Error?: string; __v2PreviewSrc?: string };
    const data = Array.isArray(entry[7]) ? entry[7] : [];
    const paths = Array.isArray(data[0]) ? data[0] : [];
    const src = firstString(paths[1], paths[0]);
    if (!src) continue;

    attachments.push({
      id: String(entry[0] || index),
      src: firstString(v2Entry.__v2PreviewSrc, src) || 'css/cssimgs/attach.png',
      filename: firstString(data[1], paths[3]),
      progress: entry[3] ? 'Uploading...' : '0%',
      removable: true,
      toggleable: true,
      active: entry[6] !== false,
      status: v2Entry.__v2Error ? 'error' : (entry[3] ? 'uploading' : 'done'),
      error: v2Entry.__v2Error ? String(v2Entry.__v2Error) : undefined,
    });
  }

  return hasRealEditorAttachments(attachments) ? attachments : [];
}

function normalizeHtmlId(input: string): string {
  if (!input) return '';
  let id = String(input).trim();
  if (id.startsWith('#')) id = id.slice(1);
  return id.replace(/['"]/g, '').trim();
}

function attrsToRecord(node: LegacyParsedElementNode): Record<string, string> {
  const out: Record<string, string> = {};
  for (const attr of node.attrs) out[attr.name] = attr.value;
  return out;
}

function domAttrsToRecord(node: HTMLElement): Record<string, string> {
  if (!node.getAttributeNames) return {};
  const out: Record<string, string> = {};
  for (const name of node.getAttributeNames()) {
    const value = node.getAttribute(name);
    if (value != null) out[name] = value;
  }
  return out;
}

function defaultChromeVisible(contract: string): boolean | undefined {
  if (contract === 'workspace.main') return true;
  if (
    contract === 'workspace.editor' ||
    contract === 'workspace.attachments' ||
    contract === 'workspace.favs' ||
    contract === 'workspace.trash' ||
    contract === 'workspace.drafts'
  ) return false;
  return undefined;
}

function extractElIdFromAttrs(attrs: Record<string, string>): number | null {
  const raw = normalizeHtmlId(attrs.id || '');
  if (!raw.startsWith('mcss-li_id-cssm_')) return null;
  const elId = parseInt(raw.replace('mcss-li_id-cssm_', ''), 10);
  return Number.isNaN(elId) ? null : elId;
}

function parseStyleString(value: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of value.split(';')) {
    const i = part.indexOf(':');
    if (i < 0) continue;
    const name = part.slice(0, i).trim();
    const val = part.slice(i + 1).trim();
    if (name) out.set(name, val);
  }
  return out;
}

function styleMapToString(styleMap: Map<string, string>): string {
  const parts: string[] = [];
  for (const [k, v] of styleMap) parts.push(`${k}:${v}`);
  return parts.join(';');
}

function setClassOnAttrs(
  attrs: Record<string, string>,
  op: 'add' | 'remove' | 'toggle',
  cls: string
): Record<string, string> {
  const classes = new Set((attrs.class || '').split(/\s+/).filter(Boolean));
  for (const one of cls.split(/\s+/).filter(Boolean)) {
    if (op === 'add') classes.add(one);
    else if (op === 'remove') classes.delete(one);
    else if (classes.has(one)) classes.delete(one);
    else classes.add(one);
  }
  return { ...attrs, class: ' ' + Array.from(classes).join(' ') + ' ' };
}

function setCssOnAttrs(
  attrs: Record<string, string>,
  prop: string,
  value: string | null | undefined
): Record<string, string> {
  const styleMap = parseStyleString(attrs.style || '');
  if (value === '' || value == null) styleMap.delete(prop);
  else styleMap.set(prop, String(value));
  const nextStyle = styleMapToString(styleMap);
  const next = { ...attrs };
  if (nextStyle) next.style = nextStyle;
  else delete next.style;
  return next;
}

function hasClassOnAttrs(attrs: Record<string, string>, cls: string): boolean {
  return (attrs.class || '').split(/\s+/).filter(Boolean).includes(cls);
}

function getCssFromAttrs(attrs: Record<string, string>, prop: string): string {
  return parseStyleString(attrs.style || '').get(prop) || '';
}

function getTextFromNode(nodeId: string, nodesById: Record<string, SubsNodeData>): string {
  const node = nodesById[nodeId];
  if (!node) return '';
  if (node.kind === 'text') return node.text;
  return node.childIds.map(childId => getTextFromNode(childId, nodesById)).join('');
}

function legacySectionId(section: LegacySectionInput): 0 | 1 {
  return section === 'submsges' || section === 1 ? 1 : 0;
}

function isActiveProjectionMode(mode?: string): boolean {
  return !mode || mode === 'active' || mode === 'msg' || mode === 'answers' || mode === 'submsges' || mode === 'subs';
}

function normalizeMoveCheckedOptions(options?: MoveCheckedOptions, fallbackSection: LegacySectionInput = 'answers'): Required<MoveCheckedOptions> {
  return {
    section: options?.section ?? fallbackSection,
    mode: options?.mode || currentLegacyPagingMode(),
    reason: options?.reason || '',
  };
}

function isLegacyDataContainerId(idVal: string): boolean {
  return idVal === 'mcss-init_data-cssm' || idVal === 'mcss-msges_data-cssm' || idVal === 'mcss-msges_data_json-cssm';
}

// Extraction + entity decoding live in payloadJson.ts (pure, shared with the
// SSR path so server and client seed identical strings). The decode runs AFTER
// the parse: decoding the container string breaks the JSON on any row whose
// text carries an escaped quote.
function extractDataContainerJson(html: string, id: string): string | null {
  return extractPayloadSpanJson(html, id);
}

const legacyDataContainerIds: Record<LegacyDataContainerKind, LegacyDataContainerState['id']> = {
  init: 'mcss-init_data-cssm',
  msges: 'mcss-msges_data-cssm',
  msgesDataJson: 'mcss-msges_data_json-cssm',
};

function mirrorLegacyDataContainerForLegacy(kind: LegacyDataContainerKind, rawText: string): void {
  const legacyKind = kind === 'init' ? 'init' : 'msges';
  getMsgDeskRuntime().facade.setDataPayload(legacyKind, rawText);
}

function defaultDisplayForElement(node: HTMLElement): string {
  const tag = node.tagName.toLowerCase();
  if (tag === 'tr') return 'table-row';
  if (tag === 'td' || tag === 'th') return 'table-cell';
  if (tag === 'tbody') return 'table-row-group';
  if (tag === 'thead') return 'table-header-group';
  if (tag === 'tfoot') return 'table-footer-group';
  if (tag === 'table') return 'table';
  if (tag === 'li') return 'list-item';
  if (tag === 'span' || tag === 'a' || tag === 'img' || tag === 'input' || tag === 'select' || tag === 'textarea' || tag === 'label') return 'inline';
  return 'block';
}

type Listener = () => void;

// The chrome contracts forceWorkspaceVisibility owns (MsgDeskApp): during the
// S1 initial-route adoption hold, direct legacy visibility writes to these are
// swallowed so the SSR chrome persists until the route mode is reached.
const MODE_OWNED_CHROME_CONTRACTS = new Set([
  'workspace.main',
  'workspace.editor',
  'workspace.attachments',
  'workspace.favs',
  'workspace.trash',
  'workspace.drafts',
  'main.root',
  'subs.toolbar',
  'subs.answers.front',
  'subs.submsges.front',
  'editor.root',
]);

class DataTreeStore {
  private state: SubsStoreState = emptyState();
  private listeners: Set<Listener> = new Set();
  private contractNodes: Map<string, HTMLElement> = new Map();
  private editorDynamicSlot = 100000;
  // S1 folder-route adoption hold (docs/27 §4 item 4): while the legacy
  // runtime is still navigating to the SSR route's desk mode, projection
  // writes that would stomp the server-rendered view (mode reset to 'msg',
  // workspace visibility flips from legacy's transient main pass, empty root
  // lists before the folder import lands) are held. Released when the
  // projection reaches a non-boot mode / the first real row projection
  // arrives, or by the app host's safety timeout.
  private initialProjectionHold: string | null = null;
  private holdEmptyShowArray = false;

  public subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
        this.listeners.delete(listener)
    };
  };

  private notify = () => {
    this.listeners.forEach(l => l());
  };

  public getState = () => this.state;

  // S1 (docs/27 §4): the client island seeds the singleton synchronously from
  // the boot payload BEFORE hydration, so the first client render matches the
  // server-rendered HTML. One-shot: once any state exists (seeded or mutated),
  // later calls are ignored. Never call this on the server — the module
  // singleton is shared across requests there; the server render receives its
  // per-request state through the provider's initialState prop instead.
  public seedInitialState = (state: SubsStoreState): boolean => {
    if (this.state.syncVersion > 0) return false;
    this.state = state;
    this.notify();
    return true;
  };

  public getContractNode = (contract: string): HTMLElement | null =>
    this.contractNodes.get(contract) || null;

  private setState = (updater: (prev: SubsStoreState) => SubsStoreState) => {
    this.state = updater(this.state);
    this.notify();
  };

  private _runNormalization(parsedNodes: LegacyParsedNode[], options?: any): SubsStoreState {
      const prevState = this.state;
      const nextNodesById = { ...prevState.nodesById };
      const nextNodesByElId = { ...prevState.nodesByElId };
      const nextHtmlIdToNodeId = { ...prevState.htmlIdToNodeId };
      const nextChildrenMap = { ...prevState.childrenMap };
      const nextActiveIds = [...prevState.activeIds];
      let nextSeq = prevState.nextNodeSeq;

      const newNodeId = () => `subs_node_${nextSeq++}`;

      const addText = (text: string) => {
        const id = newNodeId();
        nextNodesById[id] = { kind: 'text', id, text };
        return id;
      };

      const addElement = (node: LegacyParsedElementNode, childIds: string[]) => {
        const id = newNodeId();
        const attrs = attrsToRecord(node);
        const htmlId = normalizeHtmlId(attrs.id || '');
        nextNodesById[id] = {
          kind: 'element',
          id,
          tag: node.tag,
          slot: node.slot,
          attrs,
          childIds,
          htmlId: htmlId || undefined,
        };
        if (htmlId) nextHtmlIdToNodeId[htmlId] = id;
        return id;
      };

      const normalizeNodes = (nodes: LegacyParsedNode[], parentElId?: number, isSubmsges = false): string[] => {
        const resultIds: string[] = [];
        for (const node of nodes) {
          if (!isElementNode(node)) {
            resultIds.push(addText(node.value));
            continue;
          }

          const attrs = attrsToRecord(node);
          const idVal = normalizeHtmlId(attrs.id || '');
          if (isLegacyDataContainerId(idVal)) {
            // Data payload extraction moved to parseAndNormalize (regex-based) to avoid
            // htmlparser2 truncation when JSON contains embedded HTML tags.
          }

          const elId = extractElIdFromAttrs(attrs);
          if (elId !== null) {
            const childIds = normalizeNodes(node.children, elId, false);
            const rootNodeId = addElement(node, childIds);
            nextNodesByElId[elId] = [rootNodeId];

            if (parentElId) {
              if (!nextChildrenMap[parentElId]) nextChildrenMap[parentElId] = { answers: [], submsges: [] };
              const list = isSubmsges ? nextChildrenMap[parentElId].submsges : nextChildrenMap[parentElId].answers;
              if (!list.includes(elId)) {
                if (options?.mode === 'prepend') list.unshift(elId);
                else list.push(elId);
              }
            } else if (!nextActiveIds.includes(elId)) {
              if (options?.mode === 'prepend') nextActiveIds.unshift(elId);
              else nextActiveIds.push(elId);
            }
            continue;
          }

          const isAnswersUL = idVal.startsWith('mcss-ul_id-cssm_') && !idVal.endsWith('_submsges');
          const isSubmsgesUL = idVal.endsWith('_submsges');
          const childIds = normalizeNodes(node.children, parentElId, isSubmsgesUL ? true : (isAnswersUL ? false : isSubmsges));
          resultIds.push(addElement(node, childIds));
        }
        return resultIds;
      };

      let parentId: number | undefined;
      let targetIsSubmsges = false;
      if (options?.target?.id) {
        const targetId = normalizeHtmlId(options.target.id);
        if (targetId.startsWith('mcss-ul_id-cssm_')) {
          parentId = parseInt(targetId.replace('mcss-ul_id-cssm_', ''), 10);
          if (targetId.endsWith('_submsges')) targetIsSubmsges = true;
        }
      }

      const topResultIds = normalizeNodes(parsedNodes, parentId, targetIsSubmsges);

      // Seed subStates from initial HTML attrs for all sub-shaped nodes
      const nextSubStates: Record<number, SubUiState> = { ...prevState.subStates };
      for (const [htmlId, nodeId] of Object.entries(nextHtmlIdToNodeId)) {
        const elId = extractElIdFromHtmlId(htmlId);
        if (elId === null) continue;
        const node = nextNodesById[nodeId];
        if (!node || node.kind !== 'element') continue;
        if (!nextSubStates[elId]) nextSubStates[elId] = defaultSubUiState();
        const st = nextSubStates[elId];
        const classes = new Set((node.attrs.class || '').split(/\s+/).filter(Boolean));
        const styleMap = parseStyleString(node.attrs.style || '');
        if (htmlId === `mcss-li_id-cssm_${elId}`) {
          st.isVisible = !classes.has('mcss-display_none-cssm');
          const op = parseFloat(styleMap.get('opacity') || '1');
          if (!isNaN(op)) st.opacity = op;
        } else if (htmlId === `mcss-fav-cssm_${elId}`) {
          if (classes.has('mcss-ic_fav-cssm')) st.isFav = true;
          else if (classes.has('mcss-ic_fav_grey-cssm')) st.isFav = false;
          st.isFavIconOff = classes.has('mcss-ic_off-cssm');
        } else if (htmlId === `mcss-qr-cssm_${elId}`) {
          st.isQrOn = classes.has('mcss-ic_qr_on-cssm');
        } else if (htmlId === `mcss-msg_icon-cssm_${elId}`) {
          st.isHoverHighlighted = classes.has('mcss-ic_ht_red-cssm');
          st.isFlagBlue = classes.has('mcss-ic_flag_small_blue-cssm');
        } else if (htmlId === `mcss-ul_id-cssm_${elId}`) {
          const op = parseFloat(styleMap.get('opacity') || '1');
          if (!isNaN(op)) st.answersOpacity = op;
        } else if (htmlId === `mcss-ul_id-cssm_${elId}_submsges`) {
          const op = parseFloat(styleMap.get('opacity') || '1');
          if (!isNaN(op)) st.submsgesOpacity = op;
        }
      }

      return {
        nodesById: nextNodesById,
        nodesByElId: nextNodesByElId,
        htmlIdToNodeId: nextHtmlIdToNodeId,
        childrenMap: nextChildrenMap,
        activeIds: nextActiveIds,
        syncVersion: prevState.syncVersion + 1,
        visibilityMap: prevState.visibilityMap,
        subStates: nextSubStates,
        nextNodeSeq: nextSeq,
        templateData: prevState.templateData,
        legacyData: prevState.legacyData,
        renderProjection: prevState.renderProjection,
        // Helper field for setHtml
        _lastNormalizedIds: topResultIds
      } as any;
  }

  public parseAndNormalize = (html: string, options?: any) => {
    const parsedNodes = parseLegacyHtmlFragmentNodes(html);
    this.setState(() => this._runNormalization(parsedNodes, options));
    this.ingestLegacyDataContainers(html, options?.isInitial ? 'initial-html' : 'legacy-html');
  };

  // Stage 2d: the boot payload has a single parsed destination — the legacy
  // model (via the mirror). treeStore keeps only the tiny boot-static `init`
  // copy (title/is_locked reads) and rawText container bookkeeping. The
  // parsed msges copy, mainBodyHtml, and rowPreviews are no longer
  // populated; their selector fallback branches remain and see undefined.
  public ingestLegacyDataContainers = (html: string, reason = 'legacy-html'): boolean => {
    const initJson = extractDataContainerJson(html, legacyDataContainerIds.init);
    const msgesJson = extractDataContainerJson(html, legacyDataContainerIds.msges);
    const msgesDataJson = extractDataContainerJson(html, legacyDataContainerIds.msgesDataJson);
    if (!initJson && !msgesJson && !msgesDataJson) return false;

    const now = Date.now();
    const nextContainers: Partial<Record<LegacyDataContainerKind, LegacyDataContainerState>> = {};
    if (initJson) {
      nextContainers.init = {
        id: legacyDataContainerIds.init,
        rawText: initJson,
        parsed: parseJsonPayload(initJson),
        consumed: false,
        reason,
        updatedAt: now,
      };
      mirrorLegacyDataContainerForLegacy('init', initJson);
    }
    if (msgesJson) {
      nextContainers.msges = {
        id: legacyDataContainerIds.msges,
        rawText: msgesJson,
        parsed: null,
        consumed: false,
        reason,
        updatedAt: now,
      };
      mirrorLegacyDataContainerForLegacy('msges', msgesJson);
    } else if (msgesDataJson) {
      mirrorLegacyDataContainerForLegacy('msgesDataJson', msgesDataJson);
    }
    if (msgesDataJson) {
      nextContainers.msgesDataJson = {
        id: legacyDataContainerIds.msgesDataJson,
        rawText: msgesDataJson,
        parsed: null,
        consumed: false,
        reason,
        updatedAt: now,
      };
    }

    this.setState(prev => ({
      ...prev,
      legacyData: {
        ...prev.legacyData,
        containers: {
          ...(prev.legacyData.containers || {}),
          ...nextContainers,
        },
        ...(initJson ? { initJson, init: parseJsonPayload(initJson) } : {}),
      },
      syncVersion: prev.syncVersion + 1,
    }));
    return true;
  };

  public ingestPhpModel = (payload: any, reason = 'php-model'): boolean => {
    const rawMain = payload?.model?.main;
    const bodyHtml = payload?.model?.body?.html;
    const main = rawMain && typeof rawMain === 'object'
      ? {
        ...rawMain,
        ...(bodyHtml !== undefined ? { html: bodyHtml, body_html: bodyHtml } : {}),
      }
      : rawMain;
    if (!payload || payload.ok !== true || !main || typeof main !== 'object') return false;

    const init = {
      SERVERTIME: Math.floor(Date.now() / 1000),
      LASTELEMENTID: Number(main.last_elid || 0),
      is_locked: !!main.showpin,
      title: main.name_key || main.namekey_subject || main.subject || main.code_key,
      source: reason,
    };
    const subsModel = payload?.model?.subs || {};
    const answerIds = phpModelSectionIds(subsModel.answers);
    const submsgIds = phpModelSectionIds(subsModel.submsges);
    const subsById = phpModelSubsById(subsModel);

    this.setState(prev => ({
      ...prev,
      activeIds: answerIds.length ? answerIds : prev.activeIds,
      legacyData: {
        ...prev.legacyData,
        phpModel: {
          ...payload,
          model: {
            ...(payload.model || {}),
            main,
          },
        },
        init: {
          ...(prev.legacyData.init || {}),
          ...init,
        },
        msges: {
          ...(prev.legacyData.msges || {}),
          subs: {
            ...((prev.legacyData.msges || {}).subs || {}),
            0: main,
            ...subsById,
          },
        },
      },
      renderProjection: {
        ...prev.renderProjection,
        rootIds: {
          answers: answerIds.length ? answerIds : prev.renderProjection.rootIds.answers,
          submsges: submsgIds.length ? submsgIds : prev.renderProjection.rootIds.submsges,
        },
      },
      syncVersion: prev.syncVersion + 1,
    }));

    return true;
  };

  // Stage 2c: subs-load responses are MIRRORED to the legacy payload cache
  // (legacy's _import_data_el reads it) but no longer parsed into
  // legacyData.msges — the model (facade.model) is the single parsed source
  // for imported subs, and row projection flows from the legacy-driven ops
  // after import, exactly as before.
  public mirrorLegacySubsResponse = (html: string, reason = 'subs-html'): boolean => {
    const msgesJson = extractDataContainerJson(html, legacyDataContainerIds.msges)
      || extractDataContainerJson(html, legacyDataContainerIds.msgesDataJson);
    const initJson = extractDataContainerJson(html, legacyDataContainerIds.init);
    if (!msgesJson && !initJson) return false;
    if (msgesJson) mirrorLegacyDataContainerForLegacy('msges', msgesJson);
    if (initJson) mirrorLegacyDataContainerForLegacy('init', initJson);
    this.setState(prev => ({ ...prev, syncVersion: prev.syncVersion + 1 }));
    return true;
  };

  public ingestPhpSubsModel = (payload: any, reason = 'php-subs-model'): boolean => {
    const subs = payload?.subs || payload;
    if (!subs || subs.ok !== true) return false;
    const section = subs.namespace === 'submsges' ? 'submsges' : subs.namespace === 'answers' ? 'answers' : null;
    if (!section) return false;

    const ids = phpModelSectionIds(subs);
    const byId: Record<number, any> = {};
    for (const item of phpModelSectionItems(subs)) {
      const elId = phpModelItemElId(item);
      if (elId > 0) byId[elId] = item;
    }

    this.setState(prev => {
      const previousIds = prev.renderProjection.rootIds[section] || [];
      const rawMode = String(subs.mode || prev.renderProjection.mode || 'msg');
      const mode = rawMode === 'active' ? 'msg' : rawMode;
      const loadMode = String(subs.load_mode || payload?.request?.load_mode || payload?.request?.mode || '');
      const shouldAppend = loadMode === 'append' || payload?.kind === 'msgdesk.subs.refresh';
      const nextIds = ids.length
        ? (shouldAppend
          ? Array.from(new Set([...previousIds, ...ids]))
          : ids)
        : previousIds;

      return {
        ...prev,
        legacyData: {
          ...prev.legacyData,
          phpSubsModels: {
            ...((prev.legacyData as any).phpSubsModels || {}),
            [section]: {
              payload,
              reason,
              updatedAt: Date.now(),
            },
          },
          msges: {
            ...(prev.legacyData.msges || {}),
            subs: {
              ...((prev.legacyData.msges || {}).subs || {}),
              ...byId,
            },
          },
        } as any,
        renderProjection: {
          ...prev.renderProjection,
          mode,
          rootIds: {
            ...prev.renderProjection.rootIds,
            [section]: nextIds,
          },
          currentPage: {
            ...prev.renderProjection.currentPage,
            [section]: Number(subs.page || prev.renderProjection.currentPage[section] || 1),
          },
          // The paging summary describes one mode; a mode change here must
          // drop it so the count-derived fallback serves until the next sync.
          paging: mode === prev.renderProjection.mode ? prev.renderProjection.paging : {},
        },
        syncVersion: prev.syncVersion + 1,
      };
    });

    return true;
  };

  public ingestLegacyMsgesJsonPayload = (payload: any, reason = 'legacy-msges-json'): boolean => {
    if (!payload || typeof payload !== 'object') return false;
    if (!Array.isArray(payload.subs_tree) && (!payload.subs || typeof payload.subs !== 'object')) return false;

    const treeProjection = projectLegacySubsTree(payload.subs_tree);
    const treeAnswerIds = treeProjection.rootIds.answers;
    const treeSubmsgIds = treeProjection.rootIds.submsges;
    const answerIds = treeAnswerIds.length ? treeAnswerIds : idsFromLegacySubsObject(payload.subs, false);
    const submsgIds = treeSubmsgIds.length ? treeSubmsgIds : idsFromLegacySubsObject(payload.subs, true);
    const rawText = JSON.stringify(payload);

    this.setState(prev => {
      const mergeIds = (previous: number[], next: number[]) => next.length
        ? Array.from(new Set([...previous, ...next]))
        : previous;

      return {
        ...prev,
        activeIds: answerIds.length ? mergeIds(prev.activeIds, answerIds) : prev.activeIds,
        childrenMap: mergeLegacyChildrenMaps(prev.childrenMap, treeProjection.childrenMap),
        legacyData: {
          ...prev.legacyData,
          msgesJson: rawText,
          msges: {
            ...(prev.legacyData.msges || {}),
            ...payload,
            subs: {
              ...((prev.legacyData.msges || {}).subs || {}),
              ...(payload.subs || {}),
            },
          },
        },
        renderProjection: {
          ...prev.renderProjection,
          rootIds: {
            answers: mergeIds(prev.renderProjection.rootIds.answers, answerIds),
            submsges: mergeIds(prev.renderProjection.rootIds.submsges, submsgIds),
          },
        },
        syncVersion: prev.syncVersion + 1,
      };
    });

    return true;
  };

  public ingestPhpEditorFormModel = (payload: any, reason = 'php-editor-form-model'): boolean => {
    if (!payload || payload.ok !== true || payload.kind !== 'msgdesk.editor.form') return false;
    const inputs = payload.editor?.inputs;
    if (!inputs || typeof inputs !== 'object') return false;
    const editorFormInputs = collectEditorFormInputsFromMsgInputs(inputs, { msg_inputs: inputs });
    const mode = String(payload.editor?.mode || '');
    const fieldValue = (name: string): string | undefined => editorFormInputs[name]?.value;
    const fieldChecked = (name: string): boolean | undefined => editorFormInputs[name]?.checked;
    const settings: EditorFormModel['settings'] = {};
    EDITOR_SETTING_NAMES.forEach(name => {
      const input = editorFormInputs[name];
      const value: EditorSettingValue | undefined = input?.type === 'checkbox'
        ? input.checked
        : input?.value;
      if (value !== undefined) settings[name] = value;
    });

    this.setState(prev => ({
      ...prev,
      templateData: {
        ...prev.templateData,
        editorFormInputs: mergeEditorFormInputs(prev.templateData.editorFormInputs || {}, editorFormInputs),
        editorFormModel: {
          mode: mode || undefined,
          isMsg: mode === 'new_msg' || mode === 'update_msg',
          isUpdate: mode === 'update_msg',
          fields: {
            codeKey: fieldValue('code_key'),
            subject: fieldValue('subject'),
            nameKey: fieldValue('name_key'),
            smartNameKey: fieldValue('name_key_smart'),
            bodyText: fieldValue('msg_text'),
            recipientInput: fieldValue('to_address'),
          },
          settings,
          pins: {
            show: { enabled: fieldChecked('is_show_pin'), value: fieldValue('show_pin') },
            edit: { enabled: fieldChecked('is_edit_pin'), value: fieldValue('edit_pin') },
          },
          productCreation: {},
          ruling: {
            timeStart: Number(fieldValue('time_start') || 0),
            timeStop: Number(fieldValue('time_stop') || 0),
            startEnabled: fieldChecked('is_rule_start'),
            stopEnabled: fieldChecked('is_rule_stop'),
            hideTimeLocked: fieldChecked('is_hide_time_locked'),
          },
          sections: {},
          source: 'react-facade',
          reason,
          syncedAt: Date.now(),
        },
      },
      legacyData: {
        ...prev.legacyData,
        runtime: {
          ...(prev.legacyData.runtime || {}),
          msgInputs: inputs,
          reason,
          syncedAt: Date.now(),
        },
      },
      syncVersion: prev.syncVersion + 1,
    }));

    return true;
  };

  public removeAnswerSubsSelector = (selector: string) => {
      this.setState(prev => {
        if (!selector.startsWith('#mcss-li_id-cssm_')) return { ...prev, syncVersion: prev.syncVersion + 1 };
        const elId = parseInt(selector.replace('#mcss-li_id-cssm_', ''), 10);
        if (Number.isNaN(elId)) return { ...prev, syncVersion: prev.syncVersion + 1 };

        const nextNodesByElId = { ...prev.nodesByElId };
        delete nextNodesByElId[elId];
        const nextActiveIds = prev.activeIds.filter(id => id !== elId);
        const nextChildrenMap: SubsStoreState['childrenMap'] = {};
        for (const [parent, value] of Object.entries(prev.childrenMap)) {
          nextChildrenMap[Number(parent)] = {
            answers: value.answers.filter(id => id !== elId),
            submsges: value.submsges.filter(id => id !== elId),
          };
        }
        const nextSubStates = { ...prev.subStates };
        delete nextSubStates[elId];
        return {
          ...prev,
          nodesByElId: nextNodesByElId,
          activeIds: nextActiveIds,
          childrenMap: nextChildrenMap,
          subStates: nextSubStates,
          syncVersion: prev.syncVersion + 1,
        };
      });
  }

  private updateElement = (htmlId: string, fn: (node: SubsElementNodeData, prev: SubsStoreState) => SubsStoreState): boolean => {
    const normalized = normalizeHtmlId(htmlId);
    const nodeId = this.state.htmlIdToNodeId[normalized];
    if (!nodeId) return false;
    let changed = false;
    this.setState(prev => {
      const node = prev.nodesById[nodeId];
      if (!node || node.kind !== 'element') return prev;
      changed = true;
      return fn(node, prev);
    });
    return changed;
  };

  private updateElementNode = (htmlId: string, fn: (node: SubsElementNodeData) => SubsElementNodeData): boolean =>
    this.updateElement(htmlId, (node, prev) => {
      const nextNode = fn(node);
      const nextHtmlIdToNodeId = { ...prev.htmlIdToNodeId };
      if (node.htmlId && node.htmlId !== nextNode.htmlId) delete nextHtmlIdToNodeId[node.htmlId];
      if (nextNode.htmlId) nextHtmlIdToNodeId[nextNode.htmlId] = nextNode.id;
      return {
        ...prev,
        nodesById: { ...prev.nodesById, [node.id]: nextNode },
        htmlIdToNodeId: nextHtmlIdToNodeId,
        syncVersion: prev.syncVersion + 1,
      };
    });

  public readElement = (htmlId: string): SubsElementNodeData | null => {
    const nodeId = this.state.htmlIdToNodeId[normalizeHtmlId(htmlId)];
    const node = nodeId ? this.state.nodesById[nodeId] : null;
    return node && node.kind === 'element' ? node : null;
  };

  private setVisibleByLiElId = (htmlId: string, isVisible: boolean): boolean => {
    const normalized = normalizeHtmlId(htmlId);
    if (!normalized.startsWith('mcss-li_id-cssm_')) return false;
    const elId = parseInt(normalized.replace('mcss-li_id-cssm_', ''), 10);
    if (Number.isNaN(elId) || !this.state.nodesByElId[elId]) return false;
    this.setState(prev => ({
      ...prev,
      visibilityMap: { ...prev.visibilityMap, [elId]: isVisible },
      syncVersion: prev.syncVersion + 1,
    }));
    return true;
  };

  private setSubClassStateFromCommand = (htmlId: string, human: HumanLegacyCommand): boolean => {
    const normalized = normalizeHtmlId(htmlId);
    const elId = extractElIdFromHtmlId(normalized);
    if (elId === null) return false;

    const cur = this.state.subStates[elId] || defaultSubUiState();
    let patch: Partial<SubUiState> | null = null;

    switch (human.name) {
      case 'hideElementByHiddenClass':
        patch = { isVisible: false };
        break;
      case 'showElementByHiddenClass':
        patch = { isVisible: true };
        break;
      case 'toggleElementHiddenClass':
        patch = { isVisible: !cur.isVisible };
        break;
      case 'markSubFavoriteOn':
        patch = { isFav: true };
        break;
      case 'removeSubFavoriteOnClass':
        patch = { isFav: false };
        break;
      case 'markSubFavoriteOff':
        patch = { isFav: false };
        break;
      case 'removeSubFavoriteOffClass':
        patch = { isFav: true };
        break;
      case 'markFavoriteIconsDisabled':
        patch = { isFavIconOff: true };
        break;
      case 'markFavoriteIconsEnabled':
        patch = { isFavIconOff: false };
        break;
      case 'markQuickReplyIconOn':
        patch = { isQrOn: true };
        break;
      case 'markQuickReplyIconOff':
        patch = { isQrOn: false };
        break;
      case 'highlightSubIcon':
        patch = { isHoverHighlighted: true };
        break;
      case 'unhighlightSubIcon':
        patch = { isHoverHighlighted: false };
        break;
      case 'markSubFlagBlue':
        patch = { isFlagBlue: true };
        break;
      case 'removeSubFlagBlueClass':
        patch = { isFlagBlue: false };
        break;
      case 'markSubFlagDefault':
        patch = { isFlagBlue: false };
        break;
      case 'removeSubFlagDefaultClass':
        patch = { isFlagBlue: true };
        break;
      default:
        return false;
    }

    this.mutate.setSubState(elId, patch);
    return true;
  };

  private applyBooleanAttrCommand = (htmlId: string, name: string, value: unknown): boolean => {
    const isOn = value === true || value === 'true' || value === name || value === 1 || value === '1';
    return isOn
      ? this.mutate.setAttr(htmlId, name, name)
      : this.mutate.removeAttr(htmlId, name);
  };

  private htmlIdForCommandTarget = (target: MsgObjCommandTarget): string | null => {
    switch (target.kind) {
      case 'sub':
        return `mcss-li_id-cssm_${target.elId}`;
      case 'subFlag':
        return `mcss-msg_icon-cssm_${target.elId}`;
      case 'favoriteIcon':
        return `mcss-fav-cssm_${target.elId}`;
      case 'quickReplyIcon':
        return `mcss-qr-cssm_${target.elId}`;
      case 'subAnswersList':
        return `mcss-ul_id-cssm_${target.elId}`;
      case 'subMessagesList':
        return `mcss-ul_id-cssm_${target.elId}_submsges`;
      case 'input':
      case 'html':
        return target.htmlId;
      case 'paging':
        return null;
    }
  };

  private callLegacyHandler = (name: string, args: unknown[]): unknown => {
    const handler = getMsgDeskRuntime().facade.legacyHandler(name);
    if (typeof handler !== 'function') {
      console.warn(`[msgObj.command] Missing legacy handler: MSG_OBJ.${name} / NEW_MSG_OBJ.${name}`);
      return undefined;
    }
    return handler(...args);
  };

  private applySemanticCommand = (command: MsgObjSemanticCommand): unknown => {
    switch (command.type) {
      case 'setActive': {
        if (command.target.kind === 'paging' && command.active && command.target.page != null) {
          this.mutate.setCurrentPage(command.target.section, command.target.page);
          return true;
        }
        const htmlId = this.htmlIdForCommandTarget(command.target);
        return htmlId ? this.mutate.setClass(htmlId, command.active ? 'add' : 'remove', 'mcss-active-cssm') : false;
      }
      case 'setChecked': {
        if (command.target.kind === 'sub' || command.target.kind === 'subFlag') {
          this.mutate.setSubState(command.target.elId, { isFlagBlue: command.checked });
          return true;
        }
        const htmlId = this.htmlIdForCommandTarget(command.target);
        return htmlId ? this.applyBooleanAttrCommand(htmlId, 'checked', command.checked) : false;
      }
      case 'checkSub': {
        return legacyOperationGate.run('mev_msg_check_sub_evm', () => {
          const result = this.callLegacyHandler('mev_msg_check_sub_evm', [command.el, command.elId, command.checked]);
          syncProjectionAfterLegacyEvent('mev_msg_check_sub_evm');
          return result;
        });
      }
      case 'checkVisibleSubs': {
        return legacyOperationGate.run('mev_msg_check_subs_evm', () => {
          const result = this.callLegacyHandler('mev_msg_check_subs_evm', [command.el, 0, legacySectionId(command.section)]);
          syncProjectionAfterLegacyEvent('mev_msg_check_subs_evm');
          return result;
        });
      }
      case 'moveCheckedTo': {
        return legacyOperationGate.run(command.reason || `moveCheckedTo:${command.toMode}`, () => {
          const moveMeta = {
            toMode: command.toMode,
            sourceMode: command.sourceMode || currentLegacyPagingMode(),
            section: legacySectionId(command.section),
            reason: command.reason || `moveCheckedTo:${command.toMode}`,
            timestamp: Date.now(),
          };
          const runtime = getMsgDeskRuntime();
          runtime.setGlobal('__MSG_LAST_SEMANTIC_MOVE_COMMAND__', moveMeta);
          runtime.setGlobal('__MSG_PENDING_SEMANTIC_MOVE_SYNC__', moveMeta);
          return this.callLegacyHandler('mev_msg_move_subs_evm', [command.el, legacySectionId(command.section), command.toMode]);
        });
      }
      case 'setVisible': {
        const htmlId = this.htmlIdForCommandTarget(command.target);
        return htmlId ? this.mutate.setVisible(htmlId, command.visible) : false;
      }
      case 'setCurrentPage':
        this.mutate.setCurrentPage(command.section, command.page);
        return true;
      case 'showArray':
        this.mutate.showArray(command.section, command.ids);
        return true;
      case 'delete': {
        if (command.target.kind === 'sub') {
          this.removeAnswerSubsSelector(`#mcss-li_id-cssm_${command.target.elId}`);
          return true;
        }
        return false;
      }
      case 'legacyEvent':
        {
          return legacyOperationGate.run(command.name, () => {
            const menuMode = command.name === 'mev_msg_menu_switcher_evm'
              ? projectionModeFromMenuSwitcher(command.args[1])
              : null;
            const previousMode = this.state.renderProjection.mode;
            try {
              return this.callLegacyHandler(command.name, command.args);
            } finally {
              if (menuMode) {
                const sections = menuMode === 'answers'
                  ? [0] as Array<0 | 1>
                  : menuMode === 'submsges'
                    ? [1] as Array<0 | 1>
                    : undefined;
                syncLegacyModeProjection(menuMode, `legacy-event:${command.name}`, { updateRenderProjection: true, sections });
                legacyOperationGate.noteProjectionSync(`legacy-event:${command.name}`);
              } else if (
                command.name === 'mev_msg_get_more_subs_evm' &&
                (previousMode === 'answers' || previousMode === 'submsges')
              ) {
                const syncMode = currentLegacyPagingMode();
                syncLegacyModeProjection(syncMode, `legacy-event:${command.name}`, {
                  updateRenderProjection: true,
                  sections: previousMode === 'answers' ? [0] : [1],
                });
                if (syncMode !== previousMode) this.mutate.setRenderMode(previousMode);
                legacyOperationGate.noteProjectionSync(`legacy-event:${command.name}`);
              } else {
                syncProjectionAfterLegacyEvent(command.name);
              }
            }
          });
        }
    }
  };

  private applyKnownLegacyCommand = (op: LegacyUiNormalizedOp, human: HumanLegacyCommand): boolean => {
    const htmlId = normalizeHtmlId(op.target.htmlId || '');
    if (!htmlId) return false;

    switch (op.op) {
      case 'class': {
        const astUpdated = this.mutate.setClass(htmlId, op.mode, op.className);
        this.setSubClassStateFromCommand(htmlId, human);
        return astUpdated;
      }
      case 'visibility': {
        if (op.value === undefined) return false;
        return this.mutate.setVisible(htmlId, op.value);
      }
      case 'opacity':
        return this.mutate.setOpacity(htmlId, op.opacity);
      case 'attr': {
        if (op.remove) return this.mutate.removeAttr(htmlId, op.name);
        if (human.futureCommand === 'setChecked' || human.futureCommand === 'setDisabled') {
          return this.applyBooleanAttrCommand(htmlId, op.name.toLowerCase(), op.value);
        }
        return this.mutate.setAttr(htmlId, op.name, String(op.value ?? ''));
      }
      case 'prop': {
        if (
          human.futureCommand === 'setChecked' ||
          human.futureCommand === 'setDisabled' ||
          human.futureCommand === 'setSelected'
        ) {
          return this.applyBooleanAttrCommand(htmlId, op.name.toLowerCase(), op.value);
        }
        return this.mutate.setAttr(htmlId, op.name, String(op.value ?? ''));
      }
      case 'style': {
        if (human.futureCommand === 'setVisible') return this.mutate.setVisible(htmlId, op.value !== 'none');
        if (human.futureCommand === 'setOpacity') {
          const opacity = typeof op.value === 'number' ? op.value : Number.parseFloat(String(op.value ?? '1'));
          return this.mutate.setOpacity(htmlId, Number.isNaN(opacity) ? 1 : opacity);
        }
        return this.mutate.setCss(htmlId, op.prop, op.value == null ? op.value : String(op.value));
      }
      case 'text':
        return this.mutate.setText(htmlId, op.value);
      case 'html':
        return this.mutate.setHtml(htmlId, op.value);
      case 'value':
        return this.mutate.setVal(htmlId, op.value);
    }
  };

  public command = {
    normalizeLegacyRjqOp: (op: Parameters<typeof normalizeLegacyRjqOp>[0]): LegacyRjqNormalizedOp =>
      normalizeLegacyRjqOp(op),

    acceptLegacyRjqOp: (op: LegacyRjqNormalizedOp): LegacyRjqNormalizedOp => {
      const normalized = normalizeLegacyRjqOp(op);
      const runtime = getMsgDeskRuntime();
      if (runtime.getGlobal('__MSG_RJQ_FACADE_TRACE__')) {
        const log = runtime.getGlobal<LegacyRjqNormalizedOp[]>('__MSG_RJQ_NORMALIZED_OPS__') || runtime.setGlobal('__MSG_RJQ_NORMALIZED_OPS__', [] as LegacyRjqNormalizedOp[]);
        log.push(normalized);
        if (log.length > 1000) log.splice(0, log.length - 1000);
      }
      return normalized;
    },

    resolveLegacyUiCommand: (op: LegacyUiNormalizedOp): HumanLegacyCommand =>
      resolveLegacyUiCommand(op),

    applyLegacyUiOp: (op: LegacyUiNormalizedOp): boolean => {
      const human = resolveLegacyUiCommand(op);
      return this.applyKnownLegacyCommand(op, human);
    },

    send: (command: MsgObjSemanticCommand): unknown =>
      this.applySemanticCommand(command),

    setActive: (target: MsgObjCommandTarget, active: boolean): unknown =>
      this.applySemanticCommand({ type: 'setActive', target, active }),

    setChecked: (target: MsgObjCommandTarget, checked: boolean): unknown =>
      this.applySemanticCommand({ type: 'setChecked', target, checked }),

    checkSub: (el: unknown, elId: number, checked: boolean): unknown =>
      this.applySemanticCommand({ type: 'checkSub', el, elId, checked }),

    checkVisibleSubs: (el: unknown, section: LegacySectionInput): unknown =>
      this.applySemanticCommand({ type: 'checkVisibleSubs', el, section }),

    moveCheckedTo: (el: unknown, section: LegacySectionInput, toMode: LegacyMoveDestination, options?: MoveCheckedOptions): unknown => {
      const moveOptions = normalizeMoveCheckedOptions(options, section);
      return this.applySemanticCommand({
        type: 'moveCheckedTo',
        el,
        section: moveOptions.section,
        toMode,
        sourceMode: moveOptions.mode,
        reason: moveOptions.reason,
      });
    },

    moveCheckedToTrash: (el: unknown, options?: MoveCheckedOptions): unknown => {
      const moveOptions = normalizeMoveCheckedOptions(options);
      return this.applySemanticCommand({
        type: 'moveCheckedTo',
        el,
        section: moveOptions.section,
        toMode: 'trash',
        sourceMode: moveOptions.mode,
        reason: moveOptions.reason || 'moveCheckedToTrash',
      });
    },

    moveCheckedToDrafts: (el: unknown, options?: MoveCheckedOptions): unknown => {
      const moveOptions = normalizeMoveCheckedOptions(options);
      return this.applySemanticCommand({
        type: 'moveCheckedTo',
        el,
        section: moveOptions.section,
        toMode: 'drafts',
        sourceMode: moveOptions.mode,
        reason: moveOptions.reason || 'moveCheckedToDrafts',
      });
    },

    restoreChecked: (el: unknown, options?: MoveCheckedOptions): unknown => {
      const moveOptions = normalizeMoveCheckedOptions(options);
      return this.applySemanticCommand({
        type: 'moveCheckedTo',
        el,
        section: moveOptions.section,
        toMode: 'subs',
        sourceMode: moveOptions.mode,
        reason: moveOptions.reason || 'restoreChecked',
      });
    },

    moveToTrash: (el: unknown, section: LegacySectionInput): unknown =>
      this.command.moveCheckedToTrash(el, { section, reason: 'moveToTrash' }),

    moveToDrafts: (el: unknown, section: LegacySectionInput): unknown =>
      this.command.moveCheckedToDrafts(el, { section, reason: 'moveToDrafts' }),

    setVisible: (target: MsgObjCommandTarget, visible: boolean): unknown =>
      this.applySemanticCommand({ type: 'setVisible', target, visible }),

    setCurrentPage: (section: SubsSection, page: number): unknown =>
      this.applySemanticCommand({ type: 'setCurrentPage', section, page }),

    showArray: (section: SubsSection, ids: number[]): unknown =>
      this.applySemanticCommand({ type: 'showArray', section, ids }),

    delete: (target: MsgObjCommandTarget): unknown =>
      this.applySemanticCommand({ type: 'delete', target }),

    legacyEvent: (name: LegacyHandlerName, ...args: unknown[]): unknown =>
      this.applySemanticCommand({ type: 'legacyEvent', name, args }),
  };

  public mutate = {
    updateTemplateData: (slot: string, type: 'text' | 'val' | 'html', value: string) => {
      const templateData = { ...this.state.templateData };
      if (type === 'text' || type === 'html') {
        const textSlots = templateData.elementToText[slot];
        if (textSlots && textSlots.length > 0) {
          templateData.text = { ...templateData.text };
          textSlots.forEach(tSlot => {
            templateData.text[tSlot] = value;
          });
        }
      } else if (type === 'val') {
        templateData.attrs = { ...templateData.attrs };
        templateData.attrs[slot] = { ...(templateData.attrs[slot] || {}), value };
      }
      this.state = {
        ...this.state,
        templateData,
        syncVersion: this.state.syncVersion + 1,
      };
      this.notify();
    },

    ingestEditorFormHtml: (html: string): boolean => {
      void html;
      throw new Error('[v2 strict] Legacy editor-form HTML parser fallback is disabled; editor data must come from msg_inputs/runtime data.');
    },

    replaceEditorFormNodeHtml: (selector: string, html: string): boolean => {
      if (isAttachmentGalleryFragmentSelector(selector)) {
        this.setState(prev => ({
          ...prev,
          templateData: {
            ...prev.templateData,
            editorAttachments: normalizeEditorAttachmentsUpdate(
              prev.templateData.editorAttachments,
              parseEditorAttachmentsFromHtml(html || ''),
            ),
          },
          syncVersion: prev.syncVersion + 1,
        }));
        return true;
      }
      if (isVisualOnlyEditorFragmentSelector(selector)) return true;
      void html;
      throw new Error(`[v2 strict] Unsupported legacy editor fragment replacement: ${selector}`);
    },

    replaceOrInsertEditorFormNodeHtml: (selector: string, insertAfterSelector: string, html: string): boolean => {
      if (isAttachmentGalleryFragmentSelector(selector) || isAttachmentGalleryFragmentSelector(insertAfterSelector)) {
        this.setState(prev => ({
          ...prev,
          templateData: {
            ...prev.templateData,
            editorAttachments: normalizeEditorAttachmentsUpdate(
              prev.templateData.editorAttachments,
              parseEditorAttachmentsFromHtml(html || ''),
            ),
          },
          syncVersion: prev.syncVersion + 1,
        }));
        return true;
      }
      if (isVisualOnlyEditorFragmentSelector(selector) || isVisualOnlyEditorFragmentSelector(insertAfterSelector)) return true;
      void html;
      throw new Error(`[v2 strict] Unsupported legacy editor fragment insertion: ${selector} after ${insertAfterSelector}`);
    },

    replaceOrAppendEditorFormNodeHtml: (selector: string, parentSelector: string, html: string): boolean => {
      if (isAttachmentGalleryFragmentSelector(selector) || isAttachmentGalleryFragmentSelector(parentSelector)) {
        this.setState(prev => ({
          ...prev,
          templateData: {
            ...prev.templateData,
            editorAttachments: normalizeEditorAttachmentsUpdate(
              prev.templateData.editorAttachments,
              parseEditorAttachmentsFromHtml(html || ''),
            ),
          },
          syncVersion: prev.syncVersion + 1,
        }));
        return true;
      }
      if (isVisualOnlyEditorFragmentSelector(selector) || isVisualOnlyEditorFragmentSelector(parentSelector)) return true;
      void html;
      throw new Error(`[v2 strict] Unsupported legacy editor fragment append: ${selector} into ${parentSelector}`);
    },

    ingestLegacyDataContainers: (html: string, reason = 'legacy-html'): boolean =>
      this.ingestLegacyDataContainers(html, reason),

    ingestPhpModel: (payload: any, reason = 'php-model'): boolean =>
      this.ingestPhpModel(payload, reason),

    ingestPhpSubsModel: (payload: any, reason = 'php-subs-model'): boolean =>
      this.ingestPhpSubsModel(payload, reason),
    mirrorLegacySubsResponse: (html: string, reason = 'subs-html'): boolean =>
      this.mirrorLegacySubsResponse(html, reason),
    ingestLegacyMsgesJsonPayload: (payload: any, reason = 'legacy-msges-json'): boolean =>
      this.ingestLegacyMsgesJsonPayload(payload, reason),

    ingestPhpEditorFormModel: (payload: any, reason = 'php-editor-form-model'): boolean =>
      this.ingestPhpEditorFormModel(payload, reason),

    consumeLegacyDataContainer: (kind: LegacyDataContainerKind) => {
      this.setState(prev => {
        const container = prev.legacyData.containers?.[kind];
        if (!container) return prev;
        return {
          ...prev,
          legacyData: {
            ...prev.legacyData,
            containers: {
              ...(prev.legacyData.containers || {}),
              [kind]: {
                ...container,
                consumed: true,
                consumedAt: Date.now(),
              },
            },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
    },

    syncLegacyRuntime: (reason = 'legacy-runtime'): boolean => {
      const msgData = readRuntimeMainMsgData();
      const msgInputs = readRuntimeMsgInputs(0) || msgData?.msg_inputs;
      if (!msgData && !msgInputs) return false;
      const editorFormInputs = collectEditorFormInputsFromMsgInputs(msgInputs, msgData);
      const editorAttachments = collectEditorAttachmentsFromRuntime();

      this.setState(prev => ({
        ...prev,
        templateData: Object.keys(editorFormInputs).length || editorAttachments.length
          ? {
              ...prev.templateData,
              editorFormInputs: mergeEditorFormInputs(prev.templateData.editorFormInputs || {}, editorFormInputs),
              editorAttachments: editorAttachments.length
                ? normalizeEditorAttachmentsUpdate(prev.templateData.editorAttachments, editorAttachments)
                : prev.templateData.editorAttachments,
            }
          : prev.templateData,
        legacyData: {
          ...prev.legacyData,
          runtime: {
            msgData,
            msgInputs,
            reason,
            syncedAt: Date.now(),
          },
        },
        syncVersion: prev.syncVersion + 1,
      }));

      return true;
    },

    setEditorFormModel: (model: EditorFormModel | undefined): void => {
      this.setState(prev => ({
        ...prev,
        templateData: {
          ...prev.templateData,
          editorFormModel: model,
        },
        syncVersion: prev.syncVersion + 1,
      }));
    },

    updateEditorFormModel: (updater: (model: EditorFormModel | undefined) => EditorFormModel | undefined): void => {
      this.setState(prev => ({
        ...prev,
        templateData: {
          ...prev.templateData,
          editorFormModel: updater(prev.templateData.editorFormModel),
        },
        syncVersion: prev.syncVersion + 1,
      }));
    },

    registerContract: (contract: string, node: HTMLElement): void => {
      this.contractNodes.set(contract, node);
      const attrs = domAttrsToRecord(node);
      const inlineDisplay = node.style?.display || '';
      const initialVisible = defaultChromeVisible(contract) ?? (inlineDisplay === 'none' ? false : undefined);
      this.setState(prev => {
        const current = prev.chromeByContract[contract];
        return {
          ...prev,
          chromeByContract: {
            ...prev.chromeByContract,
            [contract]: {
              mounted: true,
              tagName: node.tagName ? node.tagName.toLowerCase() : current?.tagName,
              attrs: current?.attrs || attrs,
              visible: current?.visible ?? initialVisible,
              classes: current?.classes || {},
              styles: current?.styles || {},
              props: current?.props || {},
              ...(current?.text !== undefined ? { text: current.text } : {}),
              ...(current?.html !== undefined ? { html: current.html } : {}),
              ...(current?.value !== undefined ? { value: current.value } : {}),
            },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
    },

    unregisterContract: (contract: string): void => {
      this.contractNodes.delete(contract);
      this.setState(prev => {
        const current = prev.chromeByContract[contract];
        if (!current) return prev;
        return {
          ...prev,
          chromeByContract: {
            ...prev.chromeByContract,
            [contract]: { ...current, mounted: false },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
    },

    // NESTED-FOLD reconciliation (subsDisclosure toggle-children): mirror the
    // legacy tree showed-counter into React so the feed can actually fold —
    // the legacy fadeToggle targets the React-owned ul and cannot hide it.
    setSubChildrenHidden: (elId: number, section: 0 | 1, hidden: boolean) => {
      this.setState(prev => {
        const current = prev.subChildrenHidden[elId] ?? [false, false]
        if (current[section] === hidden) return prev
        const next: [boolean, boolean] = [current[0], current[1]]
        next[section] = hidden
        return {
          ...prev,
          subChildrenHidden: { ...prev.subChildrenHidden, [elId]: next },
          syncVersion: prev.syncVersion + 1,
        }
      })
    },

    setContractVisible: (contract: string, visible: boolean): boolean => {
      // S1 adoption hold: legacy's transient boot-time show_mode pass drives
      // these mode-owned contracts through setVisibleElement; while the hold
      // is active those writes are swallowed (handled, not fallback). When
      // the hold releases, forceWorkspaceVisibility rewrites all of them
      // authoritatively for the reached mode.
      if (this.initialProjectionHold && MODE_OWNED_CHROME_CONTRACTS.has(contract)) return true;
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => ({
        ...prev,
        chromeByContract: {
          ...prev.chromeByContract,
          [contract]: {
            ...prev.chromeByContract[contract]!,
            visible,
          },
        },
        syncVersion: prev.syncVersion + 1,
      }));
      return true;
    },

    setContractClass: (contract: string, op: 'add' | 'remove' | 'toggle', cls: string): boolean => {
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => {
        const current = prev.chromeByContract[contract]!;
        const classes = { ...current.classes };
        for (const one of cls.split(/\s+/).filter(Boolean)) {
          if (op === 'add') classes[one] = true;
          else if (op === 'remove') classes[one] = false;
          else classes[one] = !(classes[one] ?? this.contractNodes.get(contract)?.classList.contains(one));
        }
        return {
          ...prev,
          chromeByContract: {
            ...prev.chromeByContract,
            [contract]: { ...current, classes },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
      return true;
    },

    setContractCss: (contract: string, prop: string, value: string | null | undefined): boolean => {
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => {
        const current = prev.chromeByContract[contract]!;
        return {
          ...prev,
          chromeByContract: {
            ...prev.chromeByContract,
            [contract]: {
              ...current,
              styles: { ...current.styles, [prop]: value == null ? null : String(value) },
            },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
      return true;
    },

    setContractAttr: (contract: string, name: string, value: string): boolean => {
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => {
        const current = prev.chromeByContract[contract]!;
        return {
          ...prev,
          chromeByContract: {
            ...prev.chromeByContract,
            [contract]: {
              ...current,
              attrs: { ...current.attrs, [name]: value },
            },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
      return true;
    },

    removeContractAttr: (contract: string, name: string): boolean => {
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => {
        const current = prev.chromeByContract[contract]!;
        const attrs = { ...current.attrs };
        delete attrs[name];
        return {
          ...prev,
          chromeByContract: {
            ...prev.chromeByContract,
            [contract]: { ...current, attrs },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
      return true;
    },

    setContractProp: (contract: string, name: string, value: unknown): boolean => {
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => {
        const current = prev.chromeByContract[contract]!;
        return {
          ...prev,
          chromeByContract: {
            ...prev.chromeByContract,
            [contract]: {
              ...current,
              props: { ...current.props, [name]: value },
            },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
      return true;
    },

    setContractText: (contract: string, value: string): boolean => {
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => ({
        ...prev,
        chromeByContract: {
          ...prev.chromeByContract,
          [contract]: { ...prev.chromeByContract[contract]!, text: value, html: undefined },
        },
        syncVersion: prev.syncVersion + 1,
      }));
      return true;
    },

    setContractHtml: (contract: string, value: string): boolean => {
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => ({
        ...prev,
        chromeByContract: {
          ...prev.chromeByContract,
          [contract]: { ...prev.chromeByContract[contract]!, html: value, text: undefined },
        },
        syncVersion: prev.syncVersion + 1,
      }));
      return true;
    },

    setContractVal: (contract: string, value: string): boolean => {
      if (!this.state.chromeByContract[contract]) return false;
      this.setState(prev => ({
        ...prev,
        chromeByContract: {
          ...prev.chromeByContract,
          [contract]: { ...prev.chromeByContract[contract]!, value },
        },
        syncVersion: prev.syncVersion + 1,
      }));
      return true;
    },

    getContractAttr: (contract: string, name: string): string | undefined =>
      this.state.chromeByContract[contract]?.attrs[name],

    getContractCss: (contract: string, prop: string): string =>
      this.state.chromeByContract[contract]?.styles[prop] ?? '',

    getContractText: (contract: string): string =>
      this.state.chromeByContract[contract]?.text ?? this.contractNodes.get(contract)?.textContent ?? '',

    getContractVal: (contract: string): string =>
      this.state.chromeByContract[contract]?.value ?? (this.contractNodes.get(contract) as HTMLInputElement | null)?.value ?? '',

    hasContractClass: (contract: string, cls: string): boolean => {
      const state = this.state.chromeByContract[contract];
      if (!state) return false;
      if (state.classes[cls] !== undefined) return state.classes[cls];
      return !!this.contractNodes.get(contract)?.classList.contains(cls);
    },

    hasContract: (contract: string): boolean =>
      !!this.state.chromeByContract[contract],

    projectChromeState: (): void => {
      const mode = this.state.renderProjection.mode;
      for (const [contract, chrome] of Object.entries(this.state.chromeByContract) as Array<[string, ChromeElState]>) {
        const node = this.contractNodes.get(contract);
        if (!node || !chrome.mounted) continue;
        const visible = contract === 'subs.answers.front'
          ? mode !== 'submsges'
          : contract === 'subs.submsges.front'
            ? mode !== 'answers'
            : chrome.visible;

        if (visible === false) {
          node.setAttribute('data-md-react-display', 'none');
          node.classList.add('hidden');
          node.style.display = 'none';
        }
        else if (visible === true) {
          node.removeAttribute('data-md-react-display');
          node.classList.remove('hidden');
          if (getComputedStyle(node).display === 'none') node.style.display = defaultDisplayForElement(node);
        }
        // visible === undefined: the store has NO opinion about this contract's
        // visibility, so it must not touch it. It used to fall into the "show"
        // branch and strip `hidden` — which silently un-hid any React-owned
        // element whose CLOSED state is that class (the sidebar's "+" dropdown
        // was therefore open from first paint). Un-hiding is now only done when
        // something actually asked for it.

        for (const [cls, enabled] of Object.entries(chrome.classes)) {
          node.classList.toggle(cls, enabled);
        }

        for (const [name, value] of Object.entries(chrome.attrs)) {
          if (name === 'class' || name === 'style' || name === 'data-md-contract') continue;
          if (value == null) node.removeAttribute(name);
          else node.setAttribute(name, String(value));
        }

        for (const [prop, value] of Object.entries(chrome.styles)) {
          (node.style as any)[prop] = value == null ? '' : String(value);
        }

        for (const [name, value] of Object.entries(chrome.props)) {
          (node as any)[name] = value;
          if (name === 'checked' || name === 'disabled' || name === 'selected') {
            if (value) node.setAttribute(name, name);
            else node.removeAttribute(name);
          }
        }

        if (chrome.value !== undefined && 'value' in node) {
          (node as HTMLInputElement).value = chrome.value;
        }
        if (chrome.text !== undefined && node.textContent !== chrome.text) node.textContent = chrome.text;
        if (chrome.html !== undefined && node.innerHTML !== chrome.html) node.innerHTML = chrome.html;
      }
    },

    setSubState: (elId: number, patch: Partial<SubUiState>): void => {
      this.setState(prev => {
        const cur = prev.subStates[elId] || defaultSubUiState();
        return {
          ...prev,
          subStates: { ...prev.subStates, [elId]: { ...cur, ...patch } },
          syncVersion: prev.syncVersion + 1,
        };
      });
    },

    showArray: (section: SubsSection, ids: number[]): void => {
      const cleanIds = ids.filter((id, index, arr) =>
        Number.isInteger(id) && id >= 0 && arr.indexOf(id) === index
      );
      // While the initial-route adoption hold is active, ALL root-id writes
      // are held: legacy's transient boot pass projects the main-mode rows,
      // which must not overwrite the SSR folder/tab list. Every legitimate
      // route-target projection is preceded by its own setRenderMode(target),
      // which releases the hold first (syncLegacyModeProjection order).
      if (this.initialProjectionHold) return;
      // After the hold releases, transient EMPTY projections (the target
      // page exists but its rows are not imported yet) must not blank the
      // server-rendered list. The first real row projection releases the
      // latch and everything flows normally again.
      if (this.holdEmptyShowArray) {
        if (cleanIds.length === 0) return;
        this.holdEmptyShowArray = false;
      }
      this.setState(prev => ({
        ...prev,
        activeIds: section === 'answers' ? cleanIds : prev.activeIds,
        renderProjection: {
          ...prev.renderProjection,
          rootIds: {
            ...prev.renderProjection.rootIds,
            [section]: cleanIds,
          },
        },
        syncVersion: prev.syncVersion + 1,
      }));
    },

    // S1: hold projection stomps while legacy navigates to the SSR route mode.
    setInitialProjectionHold: (mode: string | null): void => {
      this.initialProjectionHold = mode;
      this.holdEmptyShowArray = mode !== null;
    },

    getInitialProjectionHold: (): string | null => this.initialProjectionHold,

    setRenderMode: (mode: string): void => {
      if (this.initialProjectionHold) {
        // The legacy bootstrap always passes through the main-message mode
        // before the initial route navigation lands; only that transient
        // stomp is held. Any other mode (the route target, the editor, or a
        // user-driven navigation) releases the hold and applies normally.
        if (mode === 'msg' || mode === 'subs') return;
        this.initialProjectionHold = null;
      }
      this.setState(prev => {
        const activeWorkspace = mode === 'editor'
          ? 'workspace.editor'
          : mode === 'favs'
            ? 'workspace.favs'
            : mode === 'trash'
              ? 'workspace.trash'
              : mode === 'drafts'
                ? 'workspace.drafts'
                : mode === 'attachments'
                  ? 'workspace.attachments'
                  : 'workspace.main';
        const preserveProjection = prev.renderProjection.mode === mode
          || mode === 'editor'
          || prev.renderProjection.mode === 'editor'
          || (isActiveProjectionMode(prev.renderProjection.mode) && isActiveProjectionMode(mode));
        return {
          ...prev,
          chromeByContract: Object.fromEntries(Object.entries(prev.chromeByContract).map(([contract, chrome]) => {
            if (contract === 'subs.drafts.answersFront' || contract === 'subs.drafts.submsgesFront') {
              return [contract, { ...chrome, visible: mode === 'drafts' }];
            }
            if (contract === 'subs.trash.answersFront' || contract === 'subs.trash.submsgesFront') {
              return [contract, { ...chrome, visible: mode === 'trash' }];
            }
            if (contract === 'subs.favs.answersFront' || contract === 'subs.favs.submsgesFront') {
              return [contract, { ...chrome, visible: mode === 'favs' }];
            }
            if (contract === 'subs.answers.front') {
              return [contract, { ...chrome, visible: activeWorkspace === 'workspace.main' && mode !== 'submsges' }];
            }
            if (contract === 'subs.submsges.front') {
              return [contract, { ...chrome, visible: activeWorkspace === 'workspace.main' && mode !== 'answers' }];
            }
            if (
              contract === 'main.root' ||
              contract === 'subs.toolbar'
            ) {
              return [contract, { ...chrome, visible: mode === 'msg' }];
            }
            if (contract === 'editor.root') {
              return [contract, { ...chrome, visible: activeWorkspace === 'workspace.editor' }];
            }
            if (!contract.startsWith('workspace.')) return [contract, chrome];
            return [contract, { ...chrome, visible: contract === activeWorkspace }];
          })),
          renderProjection: {
            ...prev.renderProjection,
            rootIds: preserveProjection ? prev.renderProjection.rootIds : { answers: [], submsges: [] },
            currentPage: preserveProjection ? prev.renderProjection.currentPage : {},
            // The paging summary describes ONE mode; drop it on a real mode
            // switch so the count-derived fallback serves until the next sync.
            paging: preserveProjection ? prev.renderProjection.paging : {},
            mode,
            activeSection: mode === 'submsges'
              ? 'submsges'
              : mode === 'answers'
                ? 'answers'
                : prev.renderProjection.activeSection,
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
      this.mutate.projectChromeState?.();
    },

    setActiveSection: (section: SubsSection): void => {
      this.setState(prev => ({
        ...prev,
        renderProjection: {
          ...prev.renderProjection,
          activeSection: section,
        },
        syncVersion: prev.syncVersion + 1,
      }));
    },

    setSubsLoading: (info: SubsLoadingState | null): void => {
      this.setState(prev => ({
        ...prev,
        subsLoading: info,
        syncVersion: prev.syncVersion + 1,
      }));
    },

    setPooProtocol: (pooProtocol: PooClientProjection): void => {
      this.setState(prev => ({
        ...prev,
        pooProtocol,
        syncVersion: prev.syncVersion + 1,
      }))
    },

    setCurrentPage: (section: SubsSection, page: number): void => {
      this.setState(prev => ({
        ...prev,
        renderProjection: {
          ...prev.renderProjection,
          currentPage: {
            ...prev.renderProjection.currentPage,
            [section]: page,
          },
        },
        syncVersion: prev.syncVersion + 1,
      }));
    },

    setSectionPaging: (section: SubsSection, summary: SectionPagingSummary): void => {
      this.setState(prev => {
        const previous = prev.renderProjection.paging?.[section];
        if (previous
          && previous.currentPage === summary.currentPage
          && previous.totalPages === summary.totalPages
          && previous.total === summary.total
          && previous.shownThrough === summary.shownThrough
          && previous.moreCount === summary.moreCount
          && previous.pageSize === summary.pageSize) {
          return prev;
        }
        return {
          ...prev,
          renderProjection: {
            ...prev.renderProjection,
            paging: {
              ...prev.renderProjection.paging,
              [section]: summary,
            },
          },
          syncVersion: prev.syncVersion + 1,
        };
      });
    },

    setOpacity: (htmlId: string, opacity: number): boolean => {
      const normalized = normalizeHtmlId(htmlId);
      const elId = extractElIdFromHtmlId(normalized);
      if (elId === null) return false;
      // Determine which opacity field to update based on ID type
      const isAnswersList = normalized === `mcss-ul_id-cssm_${elId}`;
      const isSubmsgesList = normalized === `mcss-ul_id-cssm_${elId}_submsges`;
      if (isAnswersList) this.mutate.setSubState(elId, { answersOpacity: opacity });
      else if (isSubmsgesList) this.mutate.setSubState(elId, { submsgesOpacity: opacity });
      else this.mutate.setSubState(elId, { opacity });
      return true;
    },

    setVisible: (htmlId: string, isVisible: boolean): boolean => {
      const normalized = normalizeHtmlId(htmlId);
      const elId = extractElIdFromHtmlId(normalized);
      // Update subStates semantic flag in all cases where we know the elId
      if (elId !== null) {
        this.mutate.setSubState(elId, { isVisible });
      }
      if (this.setVisibleByLiElId(htmlId, isVisible)) return true;
      return this.updateElementNode(htmlId, node => ({ ...node, attrs: setCssOnAttrs(node.attrs, 'display', isVisible ? '' : 'none') }));
    },

    setClass: (htmlId: string, op: 'add' | 'remove' | 'toggle', cls: string) =>
      this.updateElementNode(htmlId, node => ({ ...node, attrs: setClassOnAttrs(node.attrs, op, cls) })),

    setCss: (htmlId: string, prop: string, value: string | null | undefined) =>
      this.updateElementNode(htmlId, node => ({ ...node, attrs: setCssOnAttrs(node.attrs, prop, value) })),

    setAttr: (htmlId: string, name: string, value: string) =>
      this.updateElementNode(htmlId, node => {
        const attrs = { ...node.attrs, [name]: value };
        return { ...node, attrs, htmlId: name === 'id' ? normalizeHtmlId(value) : node.htmlId };
      }),

    removeAttr: (htmlId: string, name: string) =>
      this.updateElementNode(htmlId, node => {
        const attrs = { ...node.attrs };
        delete attrs[name];
        return { ...node, attrs, htmlId: name === 'id' ? undefined : node.htmlId };
      }),

    setText: (htmlId: string, value: string) =>
      this.updateElement(htmlId, (node, prev) => {
        const textId = `subs_node_${prev.nextNodeSeq}`;
        return {
          ...prev,
          nodesById: {
            ...prev.nodesById,
            [node.id]: { ...node, childIds: [textId] },
            [textId]: { kind: 'text', id: textId, text: value },
          },
          nextNodeSeq: prev.nextNodeSeq + 1,
          syncVersion: prev.syncVersion + 1,
        };
      }),

    setHtml: (htmlId: string, html: string) => {
        const parsedNodes = parseLegacyHtmlFragmentNodes(html);
        return this.updateElement(htmlId, (node) => {
            const nextState = this._runNormalization(parsedNodes);
            const childIds = (nextState as any)._lastNormalizedIds;
            return {
                ...nextState,
                nodesById: {
                    ...nextState.nodesById,
                    [node.id]: { ...node, childIds }
                }
            };
        });
    },

    setVal: (htmlId: string, value: string) =>
      this.updateElementNode(htmlId, node => ({ ...node, attrs: { ...node.attrs, value } })),

    hasClass: (htmlId: string, cls: string): boolean => {
      const node = this.readElement(htmlId);
      return node ? hasClassOnAttrs(node.attrs, cls) : false;
    },

    getCss: (htmlId: string, prop: string): string => {
      const node = this.readElement(htmlId);
      return node ? getCssFromAttrs(node.attrs, prop) : '';
    },

    getAttr: (htmlId: string, name: string): string | undefined => this.readElement(htmlId)?.attrs[name],

    getText: (htmlId: string): string => {
      const node = this.readElement(htmlId);
      return node ? node.childIds.map(id => getTextFromNode(id, this.state.nodesById)).join('') : '';
    },
    getVal: (htmlId: string): string => this.readElement(htmlId)?.attrs.value ?? '',
    has: (htmlId: string): boolean => !!this.readElement(htmlId),
  };
}

export const treeStore = new DataTreeStore();
getMsgDeskRuntime().setGlobal('treeStore', treeStore);
configureProjectionSync(treeStore);
